import { GameState } from '../types/GameTypes';
import { StorageService } from '../services/StorageService';
import { getTodayString } from '../utils/dateUtils';

/**
 * GameStateRepository - Handles current game state persistence
 * Manages only the current active game state (single responsibility)
 */
export class GameStateRepository {
  private storageService: StorageService;
  
  // Storage key (fixed to match original exactly)
  private static readonly CURRENT_GAME_KEY = 'rstlne_current_game';

  constructor(storageService: StorageService) {
    this.storageService = storageService;
  }

  /**
   * Load current game state
   * This replicates the original StateManager.loadCurrentGameState() behavior
   */
  loadCurrentGameState(): GameState | null {
    try {
      const savedState = this.storageService.get<GameState & { date?: string }>(GameStateRepository.CURRENT_GAME_KEY);
      if (!savedState) {
        return null;
      }
      
      // HOTFIX: Clear state if stuck on SENSE word (100% RSTLNE letters)
      if (savedState.secretWord === 'SENSE') {
        console.log('Clearing stuck SENSE game state');
        this.clearCurrentGameState();
        return null;
      }
      
      // Check if the state is from today using the same logic as original
      if (!this.isCurrentGameStateFromToday(savedState)) {
        this.clearCurrentGameState();
        return null;
      }
      
      // Remove date from returned state (but keep it for validation)
      return savedState as GameState;
    } catch (e) {
      console.error('Failed to parse current game state', e);
      return null;
    }
  }

  /**
   * Save current game state with date (like original)
   * This replicates the original StateManager.saveCurrentGameState() behavior
   */
  saveCurrentGameState(state: GameState): void {
    // Add date (in YYYY-MM-DD format) using local timezone like original
    const today = getTodayString(); // Uses local timezone
    const stateWithDate = {
      ...state,
      date: today
    };
    this.storageService.set(GameStateRepository.CURRENT_GAME_KEY, stateWithDate);
  }

  /**
   * Clear current game state
   */
  clearCurrentGameState(): void {
    this.storageService.remove(GameStateRepository.CURRENT_GAME_KEY);
  }

  /**
   * Check if current game state is from today
   * This replicates the original StateManager.isCurrentGameStateFromToday() behavior
   */
  private isCurrentGameStateFromToday(state: GameState & { date?: string }): boolean {
    if (!state || !state.date) {
      return false;
    }
    
    const today = getTodayString(); // Uses local timezone like original
    return state.date === today;
  }
}
